/**
 * TanStack Query wiring.
 *
 * TWO POLICIES WORTH KNOWING:
 *
 * 1. `retry: false` for mutations, and a single retry for queries. A phone in a
 *    restaurant is offline often; the outbox is what retries writes, and it
 *    does so with the SAME idempotency key. Letting React Query also retry a
 *    mutation would send a second key for the same intent.
 *
 * 2. Short `staleTime` with `refetchInterval` only on the two screens that are
 *    genuinely live (home, tasks). Everything else refetches when the app comes
 *    back to the foreground, which is when a phone in an apron actually
 *    reappears.
 */
import { QueryClient, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from './api';
import { ApiError } from '@sonauto/api-client';
import type {
  AppealsResponse,
  AttendanceHistoryResponse,
  ColleaguesResponse,
  HomeResponse,
  InboxNotificationsResponse,
  KpiPeriodType,
  KpiResponse,
  LeaveRequestsResponse,
  MessagesResponse,
  OpenShiftsResponse,
  PayrollPreview,
  SalaryAdvancesResponse,
  ShiftSwapsResponse,
  ShiftsResponse,
  StaffNotificationsResponse,
  TaskHistoryResponse,
  TasksResponse,
} from './contracts';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        // Never retry a 4xx: a 403 will still be a 403 in two seconds, and
        // hammering it just drains the battery.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 1;
      },
    },
    mutations: { retry: false },
  },
});

export const queryKeys = {
  home: ['staff', 'home'] as const,
  tasks: ['staff', 'tasks'] as const,
  attendanceHistory: (from?: string, to?: string) => ['staff', 'attendance', from ?? '', to ?? ''] as const,
  kpi: (periodType: KpiPeriodType, periodKey?: string) =>
    ['staff', 'kpi', periodType, periodKey ?? ''] as const,
  appeals: ['staff', 'appeals'] as const,
  shifts: (from?: string, to?: string) => ['staff', 'shifts', from ?? '', to ?? ''] as const,
  payroll: ['staff', 'payroll'] as const,
  taskHistory: ['staff', 'task-history'] as const,
  leaveRequests: ['staff', 'leave-requests'] as const,
  salaryAdvances: ['staff', 'salary-advances'] as const,
  shiftSwaps: ['staff', 'shift-swaps'] as const,
  openShifts: ['staff', 'open-shifts'] as const,
  colleagues: (shiftId?: string) => ['staff', 'colleagues', shiftId ?? ''] as const,
  notifications: ['staff', 'notifications'] as const,
  inbox: ['staff', 'inbox'] as const,
  messages: (withUserId: string) => ['staff', 'messages', withUserId] as const,
} as const;

/** The landing screen. Polled because dispatch offers arrive without warning. */
export function useHome(): UseQueryResult<HomeResponse, Error> {
  return useQuery({
    queryKey: queryKeys.home,
    queryFn: ({ signal }) => api.home(signal),
    // 20s: fast enough that an urgent offer surfaces, slow enough not to cook
    // the battery over an eight-hour shift.
    refetchInterval: 20_000,
    refetchIntervalInBackground: false,
  });
}

/** Offers expire on a server deadline, so this one polls harder. */
export function useTasks(): UseQueryResult<TasksResponse, Error> {
  return useQuery({
    queryKey: queryKeys.tasks,
    queryFn: ({ signal }) => api.tasks(signal),
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });
}

export function useAttendanceHistory(
  from?: string,
  to?: string,
): UseQueryResult<AttendanceHistoryResponse, Error> {
  return useQuery({
    queryKey: queryKeys.attendanceHistory(from, to),
    queryFn: ({ signal }) => api.attendanceHistory({ from, to, limit: 100 }, signal),
  });
}

export function useKpi(periodType: KpiPeriodType, periodKey?: string): UseQueryResult<KpiResponse, Error> {
  return useQuery({
    queryKey: queryKeys.kpi(periodType, periodKey),
    queryFn: ({ signal }) => api.kpi(periodType, periodKey, signal),
  });
}

export function useAppeals(): UseQueryResult<AppealsResponse, Error> {
  return useQuery({
    queryKey: queryKeys.appeals,
    queryFn: ({ signal }) => api.appeals(signal),
  });
}

export function useShifts(from?: string, to?: string): UseQueryResult<ShiftsResponse, Error> {
  return useQuery({
    queryKey: queryKeys.shifts(from, to),
    queryFn: ({ signal }) => api.shifts({ from, to }, signal),
  });
}

export function usePayrollPreview(): UseQueryResult<PayrollPreview, Error> {
  return useQuery({
    queryKey: queryKeys.payroll,
    queryFn: ({ signal }) => api.payrollPreview(signal),
    // 404 here is a legitimate answer ("no open payroll period"), not a bug —
    // the screen renders it as an explanation rather than an error.
    retry: false,
  });
}

/** Finished work with its KPI outcome — the Lịch sử tab. */
export function useTaskHistory(enabled = true): UseQueryResult<TaskHistoryResponse, Error> {
  return useQuery({
    queryKey: queryKeys.taskHistory,
    queryFn: ({ signal }) => api.taskHistory({ limit: 100 }, signal),
    enabled,
  });
}

export function useLeaveRequests(): UseQueryResult<LeaveRequestsResponse, Error> {
  return useQuery({
    queryKey: queryKeys.leaveRequests,
    queryFn: ({ signal }) => api.leaveRequests(signal),
  });
}

export function useSalaryAdvances(): UseQueryResult<SalaryAdvancesResponse, Error> {
  return useQuery({
    queryKey: queryKeys.salaryAdvances,
    queryFn: ({ signal }) => api.salaryAdvances(signal),
  });
}

export function useShiftSwaps(): UseQueryResult<ShiftSwapsResponse, Error> {
  return useQuery({
    queryKey: queryKeys.shiftSwaps,
    queryFn: ({ signal }) => api.shiftSwaps(signal),
  });
}

export function useOpenShifts(enabled = true): UseQueryResult<OpenShiftsResponse, Error> {
  return useQuery({
    queryKey: queryKeys.openShifts,
    queryFn: ({ signal }) => api.openShifts(undefined, signal),
    enabled,
  });
}

export function useColleagues(shiftId?: string, enabled = true): UseQueryResult<ColleaguesResponse, Error> {
  return useQuery({
    queryKey: queryKeys.colleagues(shiftId),
    queryFn: ({ signal }) => api.colleagues(shiftId, signal),
    enabled,
  });
}

/**
 * The server-side notification inbox. Shared between the tab badge and the
 * Thông báo screen through the query cache — one fetch feeds both.
 */
export function useNotifications(): UseQueryResult<StaffNotificationsResponse, Error> {
  return useQuery({
    queryKey: queryKeys.notifications,
    queryFn: ({ signal }) => api.notifications({ limit: 100 }, signal),
    // A minute: realtime events and window-focus refetches cover the urgent
    // cases; this is the slow safety net for the badge.
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

/** The generic inbox, used to discover chat partners (it carries fromUserId). */
export function useInbox(): UseQueryResult<InboxNotificationsResponse, Error> {
  return useQuery({
    queryKey: queryKeys.inbox,
    queryFn: ({ signal }) => api.inbox({ limit: 200 }, signal),
  });
}

/**
 * One chat thread. Polls every 5s while mounted — the notifications backbone
 * has no per-message push topic, so the open thread refreshes on a short
 * interval plus an instant nudge from any realtime event (see ChatScreen).
 */
export function useMessages(withUserId: string | null): UseQueryResult<MessagesResponse, Error> {
  return useQuery({
    queryKey: queryKeys.messages(withUserId ?? ''),
    queryFn: ({ signal }) => api.messages(withUserId ?? '', signal),
    enabled: withUserId !== null,
    refetchInterval: 5_000,
    refetchIntervalInBackground: false,
  });
}

/** Turns any thrown value into Vietnamese copy a screen can render. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Đã xảy ra lỗi không xác định.';
}
